import type { ApiErrorCode } from '@/services/api/generated'

/**
 * How one API error code is rendered to a person.
 *
 * `trustServerDetail` marks codes whose `detail` is written for the caller and
 * is safe to show verbatim. Codes describing an internal or upstream fault
 * carry operational text instead, so those render `safeDetail` and keep the
 * server's wording out of the interface.
 *
 * `showRetryDelay` marks codes that pair with `retry_after_seconds`, and is
 * kept in step with the backend's `ApiErrorCode::isRetryable()`.
 */
interface ProblemPresentation {
  title: string
  safeDetail: string
  trustServerDetail: boolean
  showRetryDelay: boolean
}

/**
 * Every code the API can emit, with no index signature and no fallback entry.
 *
 * The `satisfies Record<ApiErrorCode, ProblemPresentation>` is load-bearing:
 * `ApiErrorCode` is generated from `contracts/openapi.json`, so a code added
 * to the backend taxonomy fails this build until it is given presentation
 * here. Do not add a default case or an index signature -- either one silently
 * restores the fallthrough this map exists to prevent.
 */
export const PROBLEM_PRESENTATION = Object.freeze({
  INVALID_REQUEST: {
    title: 'Invalid request',
    safeDetail: 'The request could not be understood. Please check your input and try again.',
    trustServerDetail: true,
    showRetryDelay: false
  },
  AUTHENTICATION_REQUIRED: {
    title: 'Sign in required',
    safeDetail: 'Please sign in to continue.',
    trustServerDetail: true,
    showRetryDelay: false
  },
  AUTHORIZATION_DENIED: {
    title: 'Access denied',
    safeDetail: 'You do not have permission to do this.',
    trustServerDetail: true,
    showRetryDelay: false
  },
  STEP_UP_REQUIRED: {
    title: 'Verification required',
    safeDetail: 'Confirm your identity with a two-factor code to continue.',
    trustServerDetail: false,
    showRetryDelay: false
  },
  PASSWORD_CONFIRMATION_REQUIRED: {
    title: 'Password confirmation required',
    safeDetail: 'Confirm your password to continue.',
    trustServerDetail: false,
    showRetryDelay: false
  },
  ADMIN_PANEL_REDIRECT_REQUIRED: {
    title: 'Admin panel redirect required',
    safeDetail: 'Sign in through the admin panel to continue.',
    trustServerDetail: false,
    showRetryDelay: false
  },
  RESOURCE_NOT_FOUND: {
    title: 'Not found',
    safeDetail: 'We could not find what you were looking for.',
    trustServerDetail: true,
    showRetryDelay: false
  },
  METHOD_NOT_ALLOWED: {
    title: 'Action not supported',
    safeDetail: 'That action is not supported here.',
    trustServerDetail: true,
    showRetryDelay: false
  },
  CONFLICT: {
    title: 'Conflict',
    safeDetail: 'This conflicts with the current state. Refresh and try again.',
    trustServerDetail: true,
    showRetryDelay: false
  },
  VALIDATION_ERROR: {
    title: 'Check your details',
    safeDetail: 'Some details need correcting.',
    trustServerDetail: false,
    showRetryDelay: false
  },
  BUSINESS_RULE_VIOLATION: {
    title: 'Cannot complete this action',
    safeDetail: 'This action is not allowed right now.',
    trustServerDetail: true,
    showRetryDelay: false
  },
  RATE_LIMITED: {
    title: 'Too many requests',
    safeDetail: 'You have made too many requests. Please wait and try again.',
    trustServerDetail: true,
    showRetryDelay: true
  },
  EXTERNAL_SERVICE_ERROR: {
    title: 'Service error',
    safeDetail: 'A service we depend on is not responding. Please try again.',
    trustServerDetail: false,
    showRetryDelay: true
  },
  SERVICE_UNAVAILABLE: {
    title: 'Temporarily unavailable',
    safeDetail: 'This is temporarily unavailable. Please try again shortly.',
    trustServerDetail: false,
    showRetryDelay: true
  },
  SESSION_EXPIRED: {
    title: 'Session expired',
    safeDetail: 'Your session has expired. Please refresh and try again.',
    trustServerDetail: true,
    showRetryDelay: true
  },
  INTERNAL_ERROR: {
    title: 'Something went wrong',
    safeDetail: 'An unexpected error occurred.',
    trustServerDetail: false,
    showRetryDelay: false
  }
}) satisfies Record<ApiErrorCode, ProblemPresentation>
